/**
 * ExpansionWelcome — the "coming to your city" experience (Aug 2026).
 *
 * Shown on the map when the viewer is out-of-market (zero trucks, or the
 * nearest truck is 40+ miles away). Instead of a dead empty map, they get:
 *   1. a growth story ("we're expanding"),
 *   2. an email waitlist (feeds `expansion_interest` — demand signal for
 *      the admin Expansion Engine),
 *   3. an owner CTA (seed the market), and
 *   4. a one-tap jump to the nearest live market.
 */
import { useEffect, useState } from "react";
import { Truck, Bell, MapPin, Storefront } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";

export const ExpansionWelcome = ({ userPos, onBrowseMarket }) => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [market, setMarket] = useState(null);

  useEffect(() => {
    if (!userPos) return;
    api.get("/expansion/nearest-market", { params: { lat: userPos.lat, lng: userPos.lng } })
      .then(({ data }) => { if (data?.found) setMarket(data); })
      .catch(() => {});
  }, [userPos]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/expansion/interest", {
        email: email.trim(),
        lat: userPos?.lat ?? null,
        lng: userPos?.lng ?? null,
        source: "map",
      });
      setSent(true);
    } catch {
      toast.error("Couldn't save that — try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-6 sm:p-8"
      data-testid="expansion-welcome"
    >
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-2xl bg-primary/15 grid place-items-center flex-none">
          <Truck weight="duotone" className="h-6 w-6 text-primary" />
        </div>
        <div className="min-w-0">
          <h3 className="font-display font-bold text-xl tracking-tight">
            TruckTrackr is rolling into new cities
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            No food trucks near you yet — but that's exactly why we want to hear from you.
            Tell us where you are and we'll let you know the moment trucks go live in your area.
          </p>
        </div>
      </div>

      {sent ? (
        <div
          className="mt-5 rounded-xl bg-primary/10 border border-primary/30 px-4 py-3 text-sm font-medium text-primary"
          data-testid="expansion-interest-success"
        >
          You're on the list — we'll email you when trucks arrive near you.
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="flex-1 h-11 rounded-full border border-border bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            data-testid="expansion-email-input"
          />
          <button
            type="submit"
            disabled={busy}
            className="h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
            data-testid="expansion-notify-btn"
          >
            <Bell weight="bold" className="h-4 w-4" />
            {busy ? "Saving…" : "Notify me"}
          </button>
        </form>
      )}

      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        {market && (
          <button
            type="button"
            onClick={() => onBrowseMarket?.(market)}
            className="flex-1 h-10 rounded-full border border-border bg-card text-sm font-medium inline-flex items-center justify-center gap-2 hover:border-primary/50 transition-colors"
            data-testid="expansion-browse-market-btn"
          >
            <MapPin weight="duotone" className="h-4 w-4 text-primary" />
            Browse {market.trucks_nearby} truck{market.trucks_nearby === 1 ? "" : "s"} near {market.label}
          </button>
        )}
        <Link
          to="/signup"
          className="flex-1 h-10 rounded-full border border-border bg-card text-sm font-medium inline-flex items-center justify-center gap-2 hover:border-primary/50 transition-colors"
          data-testid="expansion-owner-cta"
        >
          <Storefront weight="duotone" className="h-4 w-4 text-primary" />
          Own a truck? Get listed free
        </Link>
      </div>
    </div>
  );
};

export default ExpansionWelcome;
